<script>
import { DEFAULT_THEME, EDITABLE_THEME_VARIABLES } from "@/constants/default-theme"
import { setTheme } from "@/utils/set-theme"
import { isReduxiDomain } from "@/theme-helper"
import { mapGetters, mapActions } from "vuex"

export default {
    name: "ThemeCustomizerPanel",
    data() {
        return {
            editableVariables: EDITABLE_THEME_VARIABLES,
            customTheme: { ...DEFAULT_THEME },
            themeName: "",
            themeDescription: "",
            customLogo: null,
        }
    },
    computed: {
        ...mapGetters("themeCustomizer", ["isThemeCustomizerOpen"]),
        ...mapGetters("auth", { authUser: "user" }),
        ...mapGetters("reduxi", { siteInfo: "getSiteInfo" }),
        canUseCustomizer() {
            return isReduxiDomain(this.siteInfo?.host) && this.authUser?.isDemoUser === true
        },
        fontVariable() {
            return this.editableVariables.find((v) => v.key === "--font-family")
        },
        colorVariables() {
            return this.editableVariables.filter((v) => v.key !== "--font-family")
        },
    },
    mounted() {
        if (!this.canUseCustomizer) {
            return
        }
        const savedTheme = localStorage.getItem("customTheme")
        if (savedTheme) {
            this.loadTheme()
            this.applyTheme()
        } else {
            this.customTheme = { ...DEFAULT_THEME }
        }
        const savedLogo = localStorage.getItem("customLogo")
        if (savedLogo) {
            this.customLogo = savedLogo
            this.$store.commit("reduxi/setSiteLogo", savedLogo)
        }
    },
    watch: {
        isThemeCustomizerOpen(newValue) {
            if (newValue && this.canUseCustomizer) {
                this.loadTheme()
            }
        },
    },
    methods: {
        ...mapActions("themeCustomizer", ["setThemeCustomizer"]),
        loadTheme() {
            const saved = localStorage.getItem("customTheme")
            if (saved) {
                try {
                    this.customTheme = JSON.parse(saved)
                } catch (e) {
                    this.customTheme = { ...DEFAULT_THEME }
                }
            } else {
                this.customTheme = { ...DEFAULT_THEME }
            }
            const savedLogo = localStorage.getItem("customLogo")
            if (savedLogo) {
                this.customLogo = savedLogo
                this.$store.commit("reduxi/setSiteLogo", savedLogo)
            } else {
                this.customLogo = null
            }
        },
        saveTheme() {
            if (this.canUseCustomizer) {
                localStorage.setItem("customTheme", JSON.stringify(this.customTheme))
                if (this.customLogo) {
                    localStorage.setItem("customLogo", this.customLogo)
                }
            }
        },
        hexToRgba(hex, opacity) {
            if (hex.startsWith("#")) {
                hex = hex.slice(1)
            }
            const r = parseInt(hex.substring(0, 2), 16)
            const g = parseInt(hex.substring(2, 4), 16)
            const b = parseInt(hex.substring(4, 6), 16)
            return `rgba(${r}, ${g}, ${b}, ${opacity})`
        },
        applyTheme() {
            const themeWithGradient = { ...this.customTheme }
            if (
                themeWithGradient["--gradient-color-1"] &&
                themeWithGradient["--gradient-color-2"]
            ) {
                const color1 = themeWithGradient["--gradient-color-1"]
                const color2 = themeWithGradient["--gradient-color-2"]
                themeWithGradient[
                    "--theme-gradient-background"
                ] = `transparent linear-gradient(180deg, ${color1}, ${color2}) 0 0 no-repeat padding-box`
            }
            if (themeWithGradient["--theme-secondary"]) {
                themeWithGradient["--theme-secondary-transparent-50"] = this.hexToRgba(
                    themeWithGradient["--theme-secondary"],
                    0.5,
                )
            }
            if (themeWithGradient["--theme-shade"]) {
                themeWithGradient["--theme-shade-transparent-50"] = this.hexToRgba(
                    themeWithGradient["--theme-shade"],
                    0.5,
                )
            }
            if (themeWithGradient["--theme-tertiary"]) {
                themeWithGradient["--theme-tertiary-25"] = this.hexToRgba(
                    themeWithGradient["--theme-tertiary"],
                    0.25,
                )
            }
            setTheme(document.documentElement, "default", themeWithGradient, false)
            this.saveTheme()
        },
        handleVariableChange(key, value) {
            this.customTheme[key] = value
            this.applyTheme()
        },
        resetTheme() {
            this.customTheme = { ...DEFAULT_THEME }
            this.customLogo = null
            localStorage.removeItem("customTheme")
            localStorage.removeItem("customLogo")
            this.$store.commit("reduxi/setSiteLogo", null)
            this.applyTheme()
        },
        handleLogoUpload(event) {
            const file = event.target.files[0]
            if (file) {
                const reader = new FileReader()
                reader.onload = (e) => {
                    const img = new Image()
                    img.onload = () => {
                        const canvas = document.createElement("canvas")
                        const targetWidth = 150
                        const targetHeight = 42
                        canvas.width = targetWidth
                        canvas.height = targetHeight
                        const ctx = canvas.getContext("2d")
                        const imgAspectRatio = img.width / img.height
                        const targetAspectRatio = targetWidth / targetHeight
                        let drawWidth, drawHeight, offsetX, offsetY
                        if (imgAspectRatio > targetAspectRatio) {
                            drawHeight = targetHeight
                            drawWidth = img.width * (targetHeight / img.height)
                            offsetX = (targetWidth - drawWidth) / 2
                            offsetY = 0
                        } else {
                            drawWidth = targetWidth
                            drawHeight = img.height * (targetWidth / img.width)
                            offsetX = 0
                            offsetY = (targetHeight - drawHeight) / 2
                        }
                        ctx.fillStyle = "transparent"
                        ctx.fillRect(0, 0, targetWidth, targetHeight)
                        ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight)
                        this.customLogo = canvas.toDataURL()
                        this.$store.commit("reduxi/setSiteLogo", this.customLogo)
                        this.saveTheme()
                    }
                    img.src = e.target.result
                }
                reader.readAsDataURL(file)
            }
            event.target.value = ""
        },
        exportTheme() {
            const exportData = {
                name: this.themeName || "Custom Theme",
                description: this.themeDescription || "",
                variables: this.customTheme,
                exportedAt: new Date().toISOString(),
                version: "1.0",
            }

            const dataStr = JSON.stringify(exportData, null, 2)
            const dataBlob = new Blob([dataStr], { type: "application/json" })
            const url = URL.createObjectURL(dataBlob)
            const link = document.createElement("a")
            link.href = url
            link.download = `theme-${Date.now()}.json`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)
        },
        getColorValue(key) {
            const value = this.customTheme[key]
            if (!value) return "#000000"
            if (value.startsWith("rgba")) {
                const match = value.match(/\d+/g)
                if (match) {
                    const r = parseInt(match[0]).toString(16).padStart(2, "0")
                    const g = parseInt(match[1]).toString(16).padStart(2, "0")
                    const b = parseInt(match[2]).toString(16).padStart(2, "0")
                    return `#${r}${g}${b}`
                }
            }
            if (value.startsWith("#")) return value
            return "#000000"
        },
        close() {
            this.setThemeCustomizer(false)
        },
    },
}
</script>

<template>
    <div>
        <transition name="fade">
            <div v-if="isThemeCustomizerOpen" class="theme-customizer-overlay" @click.self="close">
                <div class="theme-customizer-panel">
                    <div class="panel-header">
                        <h2>{{ $t("theme_customizer.title") }}</h2>
                        <button @click="close" class="close-btn">&times;</button>
                    </div>

                    <div class="panel-content">
                        <div class="top-row">
                            <div class="logo-upload-section">
                                <h3>{{ $t("theme_customizer.logo") }}</h3>
                                <div class="logo-preview">
                                    <img
                                        v-if="customLogo"
                                        :src="customLogo"
                                        alt="Custom logo"
                                        class="preview-image"
                                    />
                                    <div v-else class="no-logo-text">
                                        {{ $t("theme_customizer.no_logo") }}
                                    </div>
                                </div>
                                <input
                                    type="file"
                                    accept="image/*"
                                    @change="handleLogoUpload"
                                    class="logo-upload-input"
                                    id="logo-upload"
                                />
                                <label for="logo-upload" class="upload-btn">{{
                                    $t("theme_customizer.upload_logo")
                                }}</label>
                            </div>

                            <div class="font-section" v-if="fontVariable">
                                <h3>{{ fontVariable.label }}</h3>
                                <div
                                    class="font-preview"
                                    :style="{ fontFamily: customTheme[fontVariable.key] }"
                                >
                                    <div class="font-preview-text">Aa Bb Cc</div>
                                    <div class="font-preview-name">
                                        {{ customTheme[fontVariable.key] }}
                                    </div>
                                </div>
                                <select
                                    :id="fontVariable.key"
                                    :value="customTheme[fontVariable.key]"
                                    @change="
                                        (e) =>
                                            handleVariableChange(fontVariable.key, e.target.value)
                                    "
                                    class="font-select"
                                >
                                    <option
                                        v-for="option in fontVariable.options"
                                        :key="option"
                                        :value="option"
                                    >
                                        {{ option }}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <h3 class="mt-4">{{ $t("theme_customizer.theme_colors") }}</h3>
                        <div class="controls-scroll">
                            <div
                                v-for="variable in colorVariables"
                                :key="variable.key"
                                class="control-item"
                            >
                                <label :for="variable.key">{{ variable.label }}</label>
                                <input
                                    v-if="variable.type === 'color'"
                                    :id="variable.key"
                                    type="color"
                                    :value="getColorValue(variable.key)"
                                    @input="
                                        (e) => handleVariableChange(variable.key, e.target.value)
                                    "
                                    class="control-input"
                                />
                                <select
                                    v-else-if="variable.type === 'select'"
                                    :id="variable.key"
                                    :value="customTheme[variable.key]"
                                    @change="
                                        (e) => handleVariableChange(variable.key, e.target.value)
                                    "
                                    class="control-input"
                                >
                                    <option
                                        v-for="option in variable.options"
                                        :key="option"
                                        :value="option"
                                    >
                                        {{ option }}
                                    </option>
                                </select>
                                <input
                                    v-else-if="variable.type === 'text'"
                                    :id="variable.key"
                                    type="text"
                                    :value="customTheme[variable.key]"
                                    @input="
                                        (e) => handleVariableChange(variable.key, e.target.value)
                                    "
                                    class="control-input control-input-text"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="panel-footer">
                        <button @click="resetTheme" class="btn-action">
                            {{ $t("theme_customizer.reset") }}
                        </button>
                        <button @click="exportTheme" class="btn-primary">
                            {{ $t("theme_customizer.download_json") }}
                        </button>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.theme-customizer-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    padding: 20px;
}

.theme-customizer-panel {
    background: #1d2832;
    border-radius: 12px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
    width: 100%;
    max-width: 800px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
}

.panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.5rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.panel-header h2 {
    margin: 0;
    font-size: 1.5rem;
    color: #fff;
}

.close-btn {
    background: none;
    border: none;
    font-size: 2rem;
    color: rgba(255, 255, 255, 0.6);
    cursor: pointer;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all 0.2s;
}

.close-btn:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
}

.panel-content {
    flex: 1;
    overflow-y: auto;
    padding: 1.5rem;
}

.top-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
    margin-bottom: 2rem;
}

.logo-upload-section {
    display: flex;
    flex-direction: column;
}

.font-section {
    display: flex;
    flex-direction: column;
}

.logo-upload-section h3,
.font-section h3 {
    font-size: 1rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 1rem;
}

.font-preview {
    width: 100%;
    height: 120px;
    border: 2px dashed rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-bottom: 1rem;
    background: rgba(0, 0, 0, 0.2);
    gap: 0.5rem;
}

.font-preview-text {
    font-size: 2rem;
    color: #fff;
    font-weight: 600;
}

.font-preview-name {
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.6);
}

.font-select {
    padding: 0.5rem;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 0.5rem;
    font-size: 0.875rem;
    background: rgba(0, 0, 0, 0.3);
    color: #fff;
    width: 100%;
}

.font-select option {
    background: #1d2832;
    color: #fff;
}

.logo-preview {
    width: 100%;
    height: 120px;
    border: 2px dashed rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1rem;
    background: rgba(0, 0, 0, 0.2);
}

.preview-image {
    width: 150px;
    height: 42px;
    object-fit: contain;
}

.no-logo-text {
    color: rgba(255, 255, 255, 0.4);
    font-size: 0.875rem;
}

.logo-upload-input {
    display: none;
}

.upload-btn {
    display: inline-block;
    padding: 0.5rem 1rem;
    background: #0db4ac;
    color: white;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.875rem;
    font-weight: 600;
    transition: background 0.2s;
}

.upload-btn:hover {
    background: #0a9a93;
}

.controls-scroll {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1.5rem;
}

.control-item {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.75rem;
}

.control-item label {
    font-size: 0.75rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.7);
    text-transform: none;
    flex-shrink: 0;
    min-width: 100px;
}

.control-input {
    padding: 0.5rem;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 0.5rem;
    font-size: 0.875rem;
    background: rgba(0, 0, 0, 0.3);
    color: #fff;
    flex: 1;
}

.control-input-text {
    width: 100%;
}

input[type="color"].control-input {
    flex: 1;
    height: 40px;
    cursor: pointer;
    border: 1px solid rgba(255, 255, 255, 0.2);
    padding: 0.25rem;
}

select.control-input {
    flex: 1;
    background: rgba(0, 0, 0, 0.3);
    color: #fff;
}

select.control-input option {
    background: #1d2832;
    color: #fff;
}

.panel-footer {
    display: flex;
    gap: 0.75rem;
    padding: 1.5rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    justify-content: flex-end;
}

.btn-action,
.btn-primary {
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    font-size: 0.875rem;
    transition: all 0.2s;
}

.btn-action {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
}

.btn-action:hover {
    background: rgba(255, 255, 255, 0.15);
}

.btn-primary {
    background: #0db4ac;
    color: white;
}

.btn-primary:hover {
    background: #0a9a93;
}
</style>
